import type { DefaultErrorShape } from "@trpc/server"
import { TRPCError } from "@trpc/server"
import { AIX_ENV, EAixEnv } from "@aix/env"

import {
  EApplicationErrorCode,
  getApplicationError,
  getApplicationErrorClientMessage,
  isApplicationErrorRetryable,
} from "./applicationError"

/**
 * Client-safe error message. Prod: returns only the generic message (no DB/provider
 * internals leak to API clients). Non-prod: appends the original error's stack/message
 * so engineers can debug. Full detail is ALWAYS captured server-side via blg.error +
 * reportErrorToSentry regardless of env — this only governs what the client sees.
 */
export const clientSafeErrorMessage = (genericMessage: string, error: unknown): string => {
  if (AIX_ENV === EAixEnv.PROD) return genericMessage
  const detail = error instanceof Error ? (error.stack ?? error.message) : String(error)
  return `${genericMessage}\n\n[non-prod detail] ${detail}`
}

/**
 * CUSTOM ERRORS
 */

export const BadDataError = (message: string) => {
  return new TRPCError({
    code: "BAD_REQUEST",
    message: message,
  })
}

export const RateLimitError = (message: string) => {
  return new TRPCError({
    code: "TOO_MANY_REQUESTS",
    message: message,
  })
}

export const NotFoundError = (message: string) => {
  return new TRPCError({
    code: "NOT_FOUND",
    message: message,
  })
}

export const DuplicateContentError = (message: string) => {
  return new TRPCError({
    code: "CONFLICT",
    message: message,
  })
}

export const UnauthorizedError = (message: string) => {
  return new TRPCError({
    code: "UNAUTHORIZED",
    message: message,
  })
}

export const ForbiddenError = (message: string) => {
  return new TRPCError({
    code: "FORBIDDEN",
    message: message,
  })
}

export const InternalServerError = (message: string) => {
  return new TRPCError({
    code: "INTERNAL_SERVER_ERROR",
    message: message,
  })
}

const APPLICATION_ERROR_TRPC_CODES: Record<EApplicationErrorCode, TRPCError["code"]> = {
  [EApplicationErrorCode.AUTH_EXPIRED]: "UNAUTHORIZED",
  [EApplicationErrorCode.CONCURRENCY_LIMITED]: "CONFLICT",
  [EApplicationErrorCode.INSUFFICIENT_CREDITS]: "BAD_REQUEST",
  [EApplicationErrorCode.INTERNAL_INVARIANT_FAILED]: "INTERNAL_SERVER_ERROR",
  [EApplicationErrorCode.PROVIDER_CONTRACT_VIOLATION]: "INTERNAL_SERVER_ERROR",
  [EApplicationErrorCode.PROVIDER_UNAVAILABLE]: "INTERNAL_SERVER_ERROR",
  [EApplicationErrorCode.REQUIRED_RESOURCE_MISSING]: "INTERNAL_SERVER_ERROR",
  [EApplicationErrorCode.USER_INPUT_REJECTED]: "UNPROCESSABLE_CONTENT",
  [EApplicationErrorCode.USER_RESOURCE_NOT_FOUND]: "NOT_FOUND",
}

export const applicationErrorToTRPCError = (error: unknown): TRPCError | undefined => {
  const applicationError = getApplicationError(error)
  if (!applicationError) return undefined

  return new TRPCError({
    code: APPLICATION_ERROR_TRPC_CODES[applicationError.code],
    message: getApplicationErrorClientMessage(error) ?? "An unexpected error occurred",
    cause: error instanceof Error ? error : undefined,
  })
}

export const rethrowTRPCOrApplicationError = (error: unknown): void => {
  if (error instanceof TRPCError || getApplicationError(error)) throw error
}

/**
 * Map a tRPC error code to its standard HTTP status. Mirrors tRPC's own
 * `getHTTPStatusCodeFromError` mapping so wire responses are conventional.
 */
export const mapTRPCErrorToErrorCode = (error: TRPCError): number => {
  switch (error.code) {
    case "BAD_REQUEST":
      return 400
    case "PARSE_ERROR":
      return 400
    case "UNAUTHORIZED":
      return 401
    case "FORBIDDEN":
      return 403
    case "NOT_FOUND":
      return 404
    case "METHOD_NOT_SUPPORTED":
      return 405
    case "TIMEOUT":
      return 408
    case "CONFLICT":
      return 409
    case "PRECONDITION_FAILED":
      return 412
    case "PAYLOAD_TOO_LARGE":
      return 413
    case "UNPROCESSABLE_CONTENT":
      return 422
    case "TOO_MANY_REQUESTS":
      return 429
    case "CLIENT_CLOSED_REQUEST":
      return 499
    case "INTERNAL_SERVER_ERROR":
      return 500
    case "NOT_IMPLEMENTED":
      return 501
    default:
      return 500
  }
}

/**
 * Build a tRPC `errorFormatter` with consistent dev-vs-prod behavior.
 *
 * Always returns: `code`, `httpStatus`, and (if a getter is supplied) `requestId`.
 *
 * In non-prod, additionally returns: `name`, `stack`, and a `cause` object
 * (`{ name, message, stack }`) when the error wraps another. Stripped in prod
 * so internals don't leak to clients.
 *
 * Usage:
 * ```ts
 * import { buildTRPCErrorFormatter } from "@aix/apiutils/src/trpcErrors"
 * import { AIX_ENV, EAixEnv } from "@aix/env"
 *
 * const t = initTRPC.context<Context>().create({
 *   transformer: superjson,
 *   errorFormatter: buildTRPCErrorFormatter<Context>({
 *     isProd: AIX_ENV === EAixEnv.PROD,
 *     getRequestId: (ctx) => ctx?.requestId,
 *   }),
 * })
 * ```
 */
export interface BuildTRPCErrorFormatterOptions<TContext> {
  isProd: boolean
  getRequestId?: (ctx: TContext | undefined) => string | undefined
}

export const buildTRPCErrorFormatter = <TContext>(options: BuildTRPCErrorFormatterOptions<TContext>) => {
  const { isProd, getRequestId } = options

  return (formatterOpts: { shape: DefaultErrorShape; error: TRPCError; ctx?: TContext }) => {
    const { shape, error, ctx } = formatterOpts
    const requestId = getRequestId?.(ctx)
    const applicationError = getApplicationError(error)

    const baseData = {
      ...shape.data,
      code: error.code,
      httpStatus: mapTRPCErrorToErrorCode(error),
      ...(requestId ? { requestId } : {}),
      ...(applicationError
        ? {
            // Preserve the v1 fields while clients migrate to the explicit public contract.
            applicationErrorCode: applicationError.code,
            applicationErrorRetryable: isApplicationErrorRetryable(error),
            applicationErrorPublicCode: applicationError.publicCode,
            applicationErrorRecoveryAction: applicationError.recoveryAction,
          }
        : {}),
    }

    if (isProd) {
      return { ...shape, data: baseData }
    }

    const cause = error.cause
    const causeShape = cause
      ? {
          name: cause.name,
          message: cause.message,
          stack: cause.stack,
        }
      : undefined

    return {
      ...shape,
      data: {
        ...baseData,
        name: error.name,
        stack: error.stack,
        cause: causeShape,
      },
    }
  }
}
